import {
  faAddressCard,
  faEllipsis,
  faEnvelopeOpenText,
  faHelmetSafety,
  faLaptopCode,
  faPeopleArrows,
  faPersonWalkingDashedLineArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { CancelToken } from 'axios';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { selectIdentity } from '../../../../store/entities/identity';
import {
  getLangNameObj,
  getWorkplaceNameObj,
  invitationCertificateIsApproved,
  invitationCertificateIsExpired,
  joinStrings2,
} from '../../../../utils/format';
import { hasRole, isExternal } from '../../../../utils/roles';
import { QcsButton } from '../../../common/basic/QcsButton';
import { QcsHoverExpandButton } from '../../../common/basic/QcsHoverExpandButton';
import { QcsTableCell } from '../../../common/basic/QcsTableCell';
import { InnerTooltipWrapper, QcsTooltip } from '../../../common/basic/QcsTooltip';
import { FaIcon } from '../../../common/FaIcon';
import { Grid } from '../../../common/grid/Grid';
import { getInvitationPersonList, invitationPersonListActions, selectInvitationPersonList } from '../../../../store/entities/invitationPersonList';
import { SectionHeader } from '../../../common/SectionHeader';
import { InvitedPersonsAddModal, InvitedPersonsAddModalData } from './InvitedPersonsAddModal';
import { InvTabWorkersAddOneTimeModal } from './InvTabWorkersAddOneTimeModal';
import { InvitedPersonsDocumentModal } from './InvitedPersonsDocumentModal';
import { InvitedPersonsBulkEditModal } from './InvitedPersonsBulkEditModal';
import { InvitedPersonsEditModal } from './InvitedPersonsEditModal';
import { InvitedPersonsOverride } from './InvitedPersonsOverride';
import { ConfirmDialog } from '../../../common/ConfirmDialog';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { Box, IconButton, Menu, MenuItem, useTheme } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { QcsListItemIcon } from '../../../common/basic/QcsListItemIcon';
import { QcsListItemText } from '../../../common/basic/QcsListItemText';
import { PathKey, useNavigation } from '../../../../utils/urlPaths';
import { InvitedPersonsSafetyEquipmentModal } from './InvitedPersonsSafetyEquipmentModal';
import { QcsTypography } from '../../../common/basic/QcsTypography';
import _ from 'lodash';
import { useInvitation } from './invitationFunctions';
import { canRevealPin, ShowPinBtn } from '../../../supplier/ShowPinBtn';
import { SupplierDetail } from '../../../supplier/SupplierDetail';
import { SupplierBasicInfo } from '../../../supplier/SupplierBasicInfo';
import { RightDrawer } from '../../../common/RightDrawer';
import { DrawerContent } from '../../../common/DrawerContent';
import { useBusinessSettings } from '../../../../hooks/useBusinessSettings';
import { CreateUpdateEntryModal, RequestEntryModal } from './RequestEntryModal';
import { InvitationType } from '@api/models/enums/InvitationType';
import { SafetyEquipmentType } from '@api/models/enums/SafetyEquipmentType';
import { SafetyEquipmentDto } from '@api/models/invitation/SafetyEquipmentDto';
import { UserRole } from '@api/models/enums/UserRole';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { InvitationDocumentResponse } from '@api/models/invitation/InvitationDocumentResponse';
import { InvitationState } from '@api/models/enums/InvitationState';
import { TrainingRunButton } from '../../../trainingRun/TrainingRunButton';
import { invitationPersonService } from '@api/services/services';

// Trainings a person can be run through: the ones they still need, or — when training in advance —
// the ones they already completed (nothing is required of them right now, but they can be retrained).
const getTrainableTrainings = (item: InvitedPersonDto) => (item.requiredTrainings?.length ? item.requiredTrainings : (item.completedTrainings ?? []));

export const InvTabWorkers: FC = () => {
  const { t, i18n, invitation, reloadInvitation, isSaving, callWithLock } = useInvitation();

  const dispatch = useAppDispatch();
  const identity = useAppSelector(selectIdentity);
  const invitationPersonList = useAppSelector(selectInvitationPersonList);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [invitedEmployeeIds, setInvitedEmployeeIds] = useState<string[]>([]);
  const [editedData, setEditedData] = useState<InvitedPersonDto>();
  const [personOverride, setPersonOverride] = useState<InvitedPersonDto>();
  const [personSafetyEquipments, setPersonSafetyEquipments] = useState<InvitedPersonDto>();
  const [checkedItems, setCheckedItems] = useState<InvitedPersonDto[]>([]);
  const [documentDataId, setDocumentDataId] = useState<string>();
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [showRequestEntryModal, setShowRequestEntryModal] = useState<boolean>(false);
  const [requestEntryData, setRequestEntryData] = useState<CreateUpdateEntryModal>();
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; person: InvitedPersonDto } | null>(null);
  const [resendPerson, setResendPerson] = useState<InvitedPersonDto>();
  const [supplierDrawerId, setSupplierDrawerId] = useState<string>();
  const theme = useTheme();
  const { navigate } = useNavigation();
  const businessSettings = useBusinessSettings();

  const isOneTime = useMemo(
    () =>
      invitation.type === InvitationType.OneTime ||
      invitation.type === InvitationType.OneTimeEntry ||
      hasRole(identity.roles, [UserRole.ExternalWorker]),
    [identity.roles, invitation.type],
  );

  const handleEdit = (item: InvitedPersonDto) => {
    setEditedData(item);
  };

  const handleEditClose = () => {
    setEditedData(undefined);
  };

  const handleUploadDocument = (item: InvitedPersonDto) => {
    setDocumentDataId(item.id);
  };

  const handleDocumentClose = () => {
    setDocumentDataId(undefined);
  };

  const handleShowPersonOverride = (item: InvitedPersonDto) => {
    setPersonOverride(item);
  };

  const handlePersonOverrideClose = () => {
    setPersonOverride(undefined);
  };

  const handleShowSafetyEquipments = (item: InvitedPersonDto) => {
    setPersonSafetyEquipments(item);
  };

  const handleCloseSafetyEquipments = () => {
    setPersonSafetyEquipments(undefined);
  };

  const handleDeletePerson = async (item: InvitedPersonDto) => {
    await callWithLock(async () => {
      await invitationPersonService.deleteById(item!.id!);
      await reloadInvitation();
    });
  };

  // Which of the row menu's four actions the signed-in user may use on this person. It is the single
  // source of truth for both the menu items and the "..." button opening it - a user allowed none of
  // them would otherwise be handed a button that opens an empty popup.
  const rowMenuActions = useCallback(
    (person: InvitedPersonDto) => {
      const showPinAllowedRoles = [
        UserRole.AdminQcs,
        UserRole.ManagerOsah,
        UserRole.ManagerWorkplace,
        UserRole.Receptionist,
        UserRole.ExternalManager,
        UserRole.ExternalWorker,
        UserRole.ExternalOneTime,
      ];

      return {
        passEntrance: !isOneTime && hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace]),
        sendTrainingRequest: hasRole(identity.roles, [
          UserRole.AdminQcs,
          UserRole.ManagerOsah,
          UserRole.ManagerWorkplace,
          UserRole.Receptionist,
          UserRole.ExternalManager,
        ]),
        showPin: hasRole(identity.roles, showPinAllowedRoles) && canRevealPin(identity, person),
        deletePerson: hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.ExternalManager]),
      };
    },
    [identity, isOneTime],
  );

  const renderData = useCallback(
    (item: InvitedPersonDto) => {
      if (hasRole(identity.roles, [UserRole.ExternalWorker]) && item.employeeId !== identity.id) return <></>;

      const startTestDisabled =
        !hasRole(identity.roles, [
          UserRole.ManagerWorkplace,
          UserRole.ManagerOsah,
          UserRole.Receptionist,
          UserRole.ExternalManager,
          UserRole.ExternalWorker,
        ]) ||
        isSaving ||
        invitation.state === InvitationState.Archived ||
        ((item.completedTrainings?.length ?? 0) < 1 && (item.requiredTrainings?.length ?? 0) < 1);

      const startTestOk = !startTestDisabled && (item.requiredTrainings?.length ?? 0) < 1;

      const uploadDocumentDisabled = isSaving || (item.documents?.length ?? 0) < 1;

      // Documents are grouped by their required certificate; multiple documents (types) can share the same certificate.
      const documentGroups = new Map<string, InvitationDocumentResponse[]>();
      for (const document of item.documents ?? []) {
        const key = document.requiredCertificate.id ?? '';
        const group = documentGroups.get(key);
        if (group) {
          group.push(document);
        } else {
          documentGroups.set(key, [document]);
        }
      }

      const uploadDocumentOk = isExternal(identity)
        ? !uploadDocumentDisabled &&
          //Each certificate needs at least one uploaded, not-expired document.
          [...documentGroups.values()].every((group) => group.some((x) => !!x.uploadedDocument && invitationCertificateIsExpired(x)))
        : !uploadDocumentDisabled &&
          //Each certificate needs at least one uploaded and approved document.
          [...documentGroups.values()].every((group) => group.some((x) => !!x.uploadedDocument && invitationCertificateIsApproved(x)));

      const safetyEquipmentsForPerson = ([] as SafetyEquipmentDto[]).concat(
        ...(invitation.workplaces ?? [])
          .filter((workplace) => item.workplaces.some((pw) => pw.id === workplace.workplace?.id))
          .map((workplace) => workplace?.safetyEquipments ?? []),
      );

      //Any workplace in person must have safety equipment or disabled.

      const showSafetyEquipmentDisabled = safetyEquipmentsForPerson.length === 0;

      const showSafetyEquipmentOk = !showSafetyEquipmentDisabled && !safetyEquipmentsForPerson.some((x) => x.type === SafetyEquipmentType.Owned);

      // A borrowed worker belongs to a different company than the invitation's own supplier.
      const isSubcontractorWorker = !!item.supplier?.id && item.supplier.id !== invitation.supplier?.id;

      const handleRowClick = () => {
        if (isOneTime || (isSubcontractorWorker && isExternal(identity))) return;
        navigate(PathKey.SupplierEmployee, { supplierId: item.supplier?.id ?? invitation.supplier!.id!, employeeId: item.employeeId! }, undefined, {
          state: { back: `/invitation/${invitation.id}/person` },
        });
      };

      return (
        <>
          <QcsTableCell onClick={handleRowClick}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <span>
                {item.name} {item.lastName}
              </span>
              {isSubcontractorWorker && (
                <QcsHoverExpandButton
                  icon={<FaIcon icon={faPeopleArrows} width={14} height={14} />}
                  color="secondary"
                  size={28}
                  ariaLabel={`${t('invitation.persons.subcontractorEmployee')}: ${getLangNameObj(i18n, item.supplier)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSupplierDrawerId(item.supplier!.id!);
                  }}
                  sx={{
                    // Black icon at rest; inherits the button's white text once expanded on hover/focus.
                    '& svg': { color: 'common.black' },
                    '&:hover svg, &:focus-visible svg': { color: 'inherit' },
                  }}
                >
                  {getLangNameObj(i18n, item.supplier) || t('invitation.persons.subcontractor')}
                </QcsHoverExpandButton>
              )}
            </Box>
          </QcsTableCell>
          <QcsTableCell>{item.email}</QcsTableCell>
          <QcsTableCell>
            {item.role === UserRole.ExternalManager
              ? t('invitation.persons.typeManager')
              : item.role === UserRole.ExternalWorker
                ? t('invitation.persons.typeWorker')
                : ''}
          </QcsTableCell>
          <QcsTableCell align="right">
            {/* EDIT EMPLOYEE */}
            {!isOneTime &&
              hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.ExternalManager]) && (
                <QcsTooltip title={t('invitation.tabs.tooltips.editEmployee')} placement="top">
                  <InnerTooltipWrapper>
                    <QcsButton stopPropagation onClick={() => handleEdit(item)} disabled={isSaving}>
                      <ManageAccountsOutlinedIcon color={isSaving ? undefined : 'success'} />
                    </QcsButton>
                  </InnerTooltipWrapper>
                </QcsTooltip>
              )}
            {hasRole(identity.roles, [
              UserRole.AdminQcs,
              UserRole.ManagerWorkplace,
              UserRole.ManagerOsah,
              UserRole.Receptionist,
              UserRole.ExternalManager,
              UserRole.ExternalWorker,
            ]) && (
              <QcsTooltip title={t('invitation.tabs.tooltips.startTest')} placement="top">
                <InnerTooltipWrapper>
                  <TrainingRunButton
                    variant="text"
                    workers={{ ...item, requiredTrainings: getTrainableTrainings(item) }}
                    disabled={startTestDisabled}
                  >
                    <FaIcon
                      icon={faLaptopCode}
                      color={startTestDisabled ? undefined : startTestOk ? theme.palette.success.main : theme.palette.warning.main}
                    />
                  </TrainingRunButton>
                </InnerTooltipWrapper>
              </QcsTooltip>
            )}
            {/* UPLOAD DOCUMENT */}
            {!isOneTime &&
              hasRole(identity.roles, [
                UserRole.AdminQcs,
                UserRole.AdminCompany,
                UserRole.ManagerOsah,
                UserRole.ManagerWorkplace,
                UserRole.ExternalManager,
              ]) && (
                <QcsTooltip title={t('invitation.tabs.tooltips.uploadDocument')} placement="top">
                  <InnerTooltipWrapper>
                    <QcsButton stopPropagation onClick={() => handleUploadDocument(item)} disabled={uploadDocumentDisabled}>
                      <FaIcon
                        icon={faAddressCard}
                        color={uploadDocumentDisabled ? undefined : uploadDocumentOk ? theme.palette.success.main : theme.palette.warning.main}
                      />
                    </QcsButton>
                  </InnerTooltipWrapper>
                </QcsTooltip>
              )}
            {/* Show safety equipments */}
            <QcsTooltip title={t('invitation.persons.safetyEquipmentTitle')} placement="top">
              <InnerTooltipWrapper>
                <QcsButton stopPropagation onClick={() => handleShowSafetyEquipments(item)} disabled={showSafetyEquipmentDisabled}>
                  <FaIcon
                    icon={faHelmetSafety}
                    color={showSafetyEquipmentDisabled ? undefined : showSafetyEquipmentOk ? theme.palette.success.main : theme.palette.warning.main}
                  />
                </QcsButton>
              </InnerTooltipWrapper>
            </QcsTooltip>
            {/* MORE MENU */}
            {Object.values(rowMenuActions(item)).some(Boolean) && (
              <QcsButton stopPropagation onClick={(e) => setMenuAnchor({ element: e.currentTarget, person: item })}>
                <FaIcon icon={faEllipsis} color={theme.palette.common.black} />
              </QcsButton>
            )}
          </QcsTableCell>
        </>
      );
    },
    [identity, isSaving, invitation, isOneTime, t, i18n, theme, navigate, rowMenuActions],
  );

  const handleAddClick = async () => {
    setInvitedEmployeeIds([]);
    setModalIsOpen(true);

    const allResponse = await invitationPersonService.query({ invitationId: invitation.id!, getAll: true });
    setInvitedEmployeeIds(allResponse.data.content?.map((x) => x.employeeId!) ?? []);
  };

  const handleModalClose = async (data?: InvitedPersonsAddModalData) => {
    if (data) {
      const invitedPersonDto: InvitedPersonDto = {
        ...data.supplierEmployee,
        id: undefined,
        activities: data.activities,
        workplaces: data.workplaces,
        employeeId: data.supplierEmployee.id ?? '',
      };

      await invitationPersonService.create(invitedPersonDto, { invitationId: invitation.id! });
      await reloadInvitation();
    }

    setModalIsOpen(false);
  };

  const handleOneTimeModalClose = async (created?: boolean) => {
    if (created) {
      await reloadInvitation();
    }
    setModalIsOpen(false);
  };

  // Checked items are snapshots from the moment they were checked - prefer the reloaded rows so worker edits are reflected
  const currentCheckedItems = checkedItems.map(
    (item) => invitationPersonList.data?.content?.find((person) => person.id === item.id) ?? item,
  );

  const handleChangeChecked = (items: InvitedPersonDto[]) => {
    setCheckedItems(items);
  };

  const handleBulkEditClose = async (saved?: boolean) => {
    setBulkEditOpen(false);
    if (!saved) return;

    // Bulk edit changes checked persons on other grid pages too, which the reloaded page does not contain
    const response = await invitationPersonService.query({ invitationId: invitation.id!, getAll: true });
    setCheckedItems((items) => items.map((item) => response.data.content?.find((person) => person.id === item.id) ?? item));
  };

  const handleItemToSelectObject = (item: InvitedPersonDto) => item;

  const handleSelectObjectGetId = (item: InvitedPersonDto) => item.id!;

  const canUseGroupTraining = () => {
    return (
      hasRole(identity.roles, [UserRole.ManagerWorkplace, UserRole.ManagerOsah, UserRole.Receptionist, UserRole.ExternalManager]) &&
      !isSaving &&
      invitation.state !== InvitationState.Archived
    );
  };

  const handleCheckIsDisabled = (item: InvitedPersonDto) => {
    if (invitation.state === InvitationState.Archived) return true;
    if (invitation.entryRequestRequired) return false;
    return !canUseGroupTraining() && (item.requiredTrainings?.length ?? 0) > 0;
  };

  const handleAddEntryRequest = () => {
    const workplaces = _.uniqBy(
      currentCheckedItems.flatMap((item) => item.workplaces),
      'id',
    );
    // Prefill the establishment only when all workplaces belong to the same one
    const establishments = _.uniqBy(
      workplaces.map((wp) => invitation.workplaces?.find((iw) => iw.workplace?.id === wp.id)?.establishment),
      'id',
    );
    setRequestEntryData({
      supplierEmployees: currentCheckedItems,
      establishment: establishments.length === 1 ? establishments[0] : undefined,
      workplaces,
    });
    setShowRequestEntryModal(true);
  };

  const oneTimeHeader = useMemo(() => {
    if (!isOneTime) {
      return null;
    }

    const establishments = _.union((invitation.workplaces ?? []).map((x) => getLangNameObj(i18n, x.establishment)));

    return (
      <div style={{ marginBottom: '1rem' }}>
        <QcsTypography variant="subtitle1">
          <strong>{`${t('invitation.oneTimeEstablishments')}:`}</strong> {joinStrings2(establishments)}
        </QcsTypography>
        <QcsTypography variant="subtitle1">
          <strong>{`${t('invitation.oneTimeWorkplaces')}:`}</strong>{' '}
          {joinStrings2(invitation.workplaces?.map((x) => getLangNameObj(i18n, x.workplace)))}
        </QcsTypography>
        <QcsTypography variant="subtitle1">
          <strong>{`${t('invitation.oneTimeActivities')}:`}</strong>{' '}
          {joinStrings2(invitation.activities?.map((x) => getLangNameObj(i18n, x.activity)))}
        </QcsTypography>
      </div>
    );
  }, [i18n, invitation.activities, invitation.workplaces, isOneTime, t]);

  useEffect(() => {
    dispatch(invitationPersonListActions.updateSearch(''));
  }, [dispatch, invitation.id]);

  const handleGetData = useCallback(
    (cancelToken: CancelToken) => {
      dispatch(getInvitationPersonList(invitation.id!, cancelToken));
    },
    [dispatch, invitation.id],
  );

  const menuActions = menuAnchor ? rowMenuActions(menuAnchor.person) : undefined;

  return (
    <>
      {isOneTime ? (
        <InvTabWorkersAddOneTimeModal open={modalIsOpen} onClose={handleOneTimeModalClose} />
      ) : (
        <InvitedPersonsAddModal
          supplierId={invitation.supplier?.id}
          initActivities={
            invitation.activities?.map((x) => ({
              id: x.activity!.id!,
              name: x.activity?.name,
              nameEn: x.activity?.nameEn,
            })) ?? []
          }
          initWorkplaces={
            invitation.workplaces?.map((x) => ({
              id: x.workplace!.id!,
              name: getWorkplaceNameObj(i18n, x.workplace, x.establishment),
            })) ?? []
          }
          open={modalIsOpen}
          onClose={handleModalClose}
          excludeIds={invitedEmployeeIds}
        />
      )}
      <InvitedPersonsEditModal data={editedData} onClose={handleEditClose} />
      {bulkEditOpen && <InvitedPersonsBulkEditModal persons={checkedItems} onClose={handleBulkEditClose} />}
      <InvitedPersonsDocumentModal data={invitationPersonList.data?.content?.find((x) => x.id === documentDataId)} onClose={handleDocumentClose} />
      <InvitedPersonsOverride data={personOverride} onClose={handlePersonOverrideClose} />
      <InvitedPersonsSafetyEquipmentModal data={personSafetyEquipments} onClose={handleCloseSafetyEquipments} />
      <RightDrawer
        open={!!supplierDrawerId}
        onClose={() => setSupplierDrawerId(undefined)}
        width={800}
        headerActions={
          supplierDrawerId && !isExternal(identity) ? (
            <IconButton
              onClick={() => navigate(PathKey.SupplierDetail, { supplierId: supplierDrawerId }, { backTo: `/invitation/${invitation.id}/person` })}
              aria-label={t('common.openFullPage')}
            >
              <OpenInFullIcon />
            </IconButton>
          ) : undefined
        }
      >
        {supplierDrawerId && (
          <DrawerContent>
            {isExternal(identity) ? <SupplierBasicInfo supplierId={supplierDrawerId} /> : <SupplierDetail forSupplierId={supplierDrawerId} />}
          </DrawerContent>
        )}
      </RightDrawer>
      {showRequestEntryModal && <RequestEntryModal data={requestEntryData} onClose={() => setShowRequestEntryModal(false)} />}
      <ConfirmDialog
        onClose={() => setResendPerson(undefined)}
        onConfirm={resendPerson ? () => invitationPersonService.resendNotification(invitation.id!, resendPerson.id!) : null}
        title={t('invitation.persons.sendTrainingRequest')}
        message={t('invitation.persons.sendTrainingRequestConfirm')}
      />

      <Menu anchorEl={menuAnchor?.element} open={!!menuAnchor} onClose={() => setMenuAnchor(null)} onClick={(e) => e.stopPropagation()}>
        {menuActions?.passEntrance && (
          <MenuItem
            onClick={() => {
              handleShowPersonOverride(menuAnchor!.person);
              setMenuAnchor(null);
            }}
            disabled={
              !!(
                menuAnchor?.person.conditionOverridden &&
                new Date(new Date(menuAnchor.person.conditionOverridden).setHours(0, 0, 0, 0)).valueOf() > new Date().valueOf()
              ) ||
              isSaving ||
              invitation.state === InvitationState.Archived
            }
          >
            <QcsListItemIcon>
              <FaIcon icon={faPersonWalkingDashedLineArrowRight} color={theme.palette.common.black} />
            </QcsListItemIcon>
            <QcsListItemText>{t('invitation.tabs.tooltips.passEntrance')}</QcsListItemText>
          </MenuItem>
        )}
        {menuActions?.sendTrainingRequest && (
          <MenuItem
            onClick={() => {
              setResendPerson(menuAnchor!.person);
              setMenuAnchor(null);
            }}
            disabled={!menuAnchor?.person.email}
          >
            <QcsListItemIcon>
              <FaIcon icon={faEnvelopeOpenText} color={theme.palette.common.black} />
            </QcsListItemIcon>
            <QcsListItemText>{t('invitation.persons.sendTrainingRequest')}</QcsListItemText>
          </MenuItem>
        )}
        {menuAnchor && menuActions?.showPin && (
          <ShowPinBtn
            person={menuAnchor.person}
            pinDisabled={isSaving}
            buttonType={'menuItem'}
            textKey={isOneTime ? 'invitation.tabs.tooltips.pinVisit' : undefined}
          />
        )}
        {menuActions?.deletePerson && (
          <MenuItem
            onClick={() => {
              handleDeletePerson(menuAnchor!.person);
              setMenuAnchor(null);
            }}
            disabled={isSaving || invitation.state === InvitationState.Archived}
          >
            <QcsListItemIcon>
              <DeleteForeverIcon color={isSaving || invitation.state === InvitationState.Archived ? undefined : 'error'} />
            </QcsListItemIcon>
            <QcsListItemText>{t(isOneTime ? 'invitation.tabs.tooltips.deleteVisit' : 'invitation.tabs.tooltips.deleteEmployee')}</QcsListItemText>
          </MenuItem>
        )}
      </Menu>

      {oneTimeHeader}

      {hasRole(
        identity.roles,
        isOneTime ? [UserRole.ManagerWorkplace, UserRole.ManagerOsah] : [UserRole.ExternalManager, UserRole.ManagerWorkplace, UserRole.ManagerOsah],
      ) && (
        <SectionHeader
          title=" "
          addText={isOneTime ? 'invitation.persons.addVisit' : 'invitation.persons.add'}
          handleAddClick={handleAddClick}
          addDisabled={invitation.state === InvitationState.Archived}
        />
      )}
      <Grid<InvitedPersonDto, InvitedPersonDto>
        headers={[
          { captionStr: 'invitation.persons.fullName' },
          { captionStr: 'invitation.persons.email' },
          { captionStr: 'invitation.persons.type' },
          {},
        ]}
        data={invitationPersonList}
        getData={handleGetData}
        gridActions={invitationPersonListActions}
        renderData={renderData}
        checkbox={!isOneTime && invitation.state !== InvitationState.Archived}
        checkedItems={checkedItems}
        changeChecked={handleChangeChecked}
        itemToSelectObject={handleItemToSelectObject}
        selectObjectGetId={handleSelectObjectGetId}
        isCheckDisabled={handleCheckIsDisabled}
      />

      {!isOneTime && hasRole(identity.roles, [UserRole.ManagerWorkplace, UserRole.ManagerOsah, UserRole.Receptionist, UserRole.ExternalManager]) && (
        <TrainingRunButton
          workers={currentCheckedItems.map((it) => ({ ...it, requiredTrainings: getTrainableTrainings(it) }))}
          disabled={currentCheckedItems.length === 0 || currentCheckedItems.every((it) => getTrainableTrainings(it).length < 1) || !canUseGroupTraining()}
          sx={{ mt: '1.5rem' }}
        >
          {t('invitation.persons.startTest')}
        </TrainingRunButton>
      )}
      {businessSettings.allowEntryRequests &&
        invitation.entryRequestRequired &&
        hasRole(identity.roles, [
          UserRole.AdminQcs,
          UserRole.ManagerOsah,
          UserRole.ManagerWorkplace,
          UserRole.ExternalManager,
          UserRole.ExternalWorker,
          UserRole.ExternalOneTime,
        ]) && (
          <QcsButton
            variant="contained"
            onClick={handleAddEntryRequest}
            disabled={checkedItems.length === 0 || isSaving || invitation.state === InvitationState.Archived}
            sx={{ mt: '1.5rem', ml: '1rem' }}
          >
            {t('invitation.persons.entryRequest')}
          </QcsButton>
        )}
      {!isOneTime && hasRole(identity.roles, [UserRole.ManagerWorkplace, UserRole.ManagerOsah]) && (
        <QcsButton
          variant="contained"
          onClick={() => setBulkEditOpen(true)}
          disabled={checkedItems.length === 0 || isSaving || invitation.state === InvitationState.Archived}
          sx={{ mt: '1.5rem', ml: '1rem' }}
        >
          {t('invitation.persons.bulkEdit')}
        </QcsButton>
      )}
    </>
  );
};
